import { EvalStatusBadge } from "@/components/eval/EvalStatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminService } from "@/gen/omnios/v1/admin_connect.js";
import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/eval/runs")({
	component: EvalRuns,
});

const adminClient = createClient(AdminService, transport);

function EvalRuns() {
	const { t } = useTranslation("eval");
	const navigate = useNavigate();
	const [statusFilter, setStatusFilter] = useState("");

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "eval-runs-list", statusFilter],
		queryFn: () =>
			adminClient.listAllEvalRuns({
				pageSize: 100,
				statusFilter,
			}),
	});

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h2 className="text-lg font-semibold">{t("all_runs")}</h2>
				<Tabs
					value={statusFilter || "all"}
					onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}
				>
					<TabsList>
						<TabsTrigger value="all">{t("filter_all")}</TabsTrigger>
						<TabsTrigger value="running">{t("filter_running")}</TabsTrigger>
						<TabsTrigger value="completed">{t("filter_completed")}</TabsTrigger>
						<TabsTrigger value="failed">{t("filter_failed")}</TabsTrigger>
					</TabsList>
				</Tabs>
			</div>

			{isLoading ? (
				<Skeleton className="h-64" />
			) : !data?.runs?.length ? (
				<Card>
					<CardContent className="py-8 text-center text-muted-foreground">
						{t("no_runs")}
					</CardContent>
				</Card>
			) : (
				<div className="rounded-md border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>{t("col_dataset")}</TableHead>
								<TableHead>{t("col_version")}</TableHead>
								<TableHead>{t("col_mode")}</TableHead>
								<TableHead>{t("col_model")}</TableHead>
								<TableHead>{t("col_status")}</TableHead>
								<TableHead>Hit@1</TableHead>
								<TableHead>Hit@3</TableHead>
								<TableHead>MRR</TableHead>
								<TableHead>NDCG@5</TableHead>
								<TableHead>{t("col_date")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{data.runs.map((run) => (
								<TableRow
									key={run.id}
									className="cursor-pointer hover:bg-muted/50"
									onClick={() =>
										navigate({
											to: "/admin/eval/runs/$id",
											params: { id: run.id },
										})
									}
								>
									<TableCell className="font-medium">
										{run.datasetName || run.datasetId.slice(0, 8)}
									</TableCell>
									<TableCell className="font-mono text-xs">
										{run.algorithmVersion || "—"}
									</TableCell>
									<TableCell className="text-xs">{run.matchMode}</TableCell>
									<TableCell className="text-xs">
										{run.modelName || "—"}
									</TableCell>
									<TableCell>
										<EvalStatusBadge status={run.status} />
									</TableCell>
									<TableCell className="font-mono">
										{run.metrics
											? `${(run.metrics.hitAt1 * 100).toFixed(1)}%`
											: "—"}
									</TableCell>
									<TableCell className="font-mono">
										{run.metrics
											? `${(run.metrics.hitAt3 * 100).toFixed(1)}%`
											: "—"}
									</TableCell>
									<TableCell className="font-mono">
										{run.metrics ? run.metrics.mrr.toFixed(3) : "—"}
									</TableCell>
									<TableCell className="font-mono">
										{run.metrics ? run.metrics.ndcgAt5.toFixed(3) : "—"}
									</TableCell>
									<TableCell className="text-xs text-muted-foreground">
										{run.createdAt
											? new Date(
													Number(run.createdAt.seconds) * 1000,
												).toLocaleDateString()
											: "—"}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}
